import React from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Gift, Package, UserRound } from 'lucide-react';

type CustomerAccountShellProps = {
  children: React.ReactNode;
};

const navClass = ({ isActive }: { isActive: boolean }) =>
  [
    'flex shrink-0 items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
    isActive
      ? 'bg-slate-900 text-white shadow-sm'
      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
  ].join(' ');

const navClassMobile = ({ isActive }: { isActive: boolean }) =>
  [
    'flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-2 text-xs font-semibold whitespace-nowrap transition-colors',
    isActive
      ? 'border-slate-900 bg-slate-900 text-white'
      : 'border-slate-200/90 bg-white text-slate-600 hover:border-slate-300',
  ].join(' ');

/**
 * Account area: fixed left rail on large screens (like workspace), horizontal pills on small screens.
 */
function CustomerAccountShell({ children }: CustomerAccountShellProps) {
  const { t } = useTranslation();

  const links = [
    { to: '/account', end: true, label: t('account.sidebar_overview'), Icon: UserRound },
    { to: '/account/orders', end: false, label: t('account.sidebar_orders'), Icon: Package },
    { to: '/account/referrals', end: false, label: t('account.sidebar_referrals'), Icon: Gift },
  ] as const;

  return (
    <div className="flex w-full flex-col gap-6 lg:gap-0">
      <nav
        className="mx-auto flex w-full max-w-6xl snap-x snap-mandatory gap-2 overflow-x-auto rounded-xl border border-slate-200/90 bg-slate-50 p-2 px-4 shadow-sm ring-1 ring-slate-900/[0.04] sm:px-6 lg:hidden"
        aria-label={t('account.title')}
      >
        {links.map(({ to, end, label, Icon }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={(args) => `${navClassMobile(args)} snap-start`}
          >
            <Icon className="h-3.5 w-3.5 shrink-0 opacity-90" aria-hidden />
            {label}
          </NavLink>
        ))}
      </nav>

      <aside
        className="hidden lg:fixed lg:bottom-0 lg:left-0 lg:z-30 lg:block lg:w-52 lg:overflow-y-auto lg:border-r lg:border-slate-200 lg:bg-white lg:px-3 lg:py-4"
        style={{ top: 'var(--storefront-header-h)' }}
        aria-label={t('account.title')}
      >
        <nav className="space-y-1 text-sm font-medium">
          {links.map(({ to, end, label, Icon }) => (
            <NavLink key={to} to={to} end={end} className={navClass}>
              <Icon className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
              {label}
            </NavLink>
          ))}
        </nav>
      </aside>

      <div className="min-w-0 border-t border-slate-200/80 pt-6 lg:ml-52 lg:border-t-0 lg:pt-8">
        <div className="mx-auto w-full max-w-6xl px-4 pb-14 sm:px-6 lg:px-8">{children}</div>
      </div>
    </div>
  );
}

export default CustomerAccountShell;
